import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { IApiRequest } from '@core/interfaces/api-request';
import { AnalyticsService } from '@core/services/analytics.service';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';
import { ModalUtilsService } from '@core/services/modal-utils.service';
import { ApiServiceHolder } from '@core/utils/api-service-holder';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';
import {
  faCheckCircle,
  faGavel,
  faLock,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { NoPaymentMethodDialogComponent } from '@shared/components/no-payment-method-dialog/no-payment-method-dialog.component';
import { BidService } from '@shared/services/bid.service';
import { EventService } from '@shared/services/event.service';
import { MessageService } from '@shared/services/message.service';
import { Observable, Subscription, filter, interval, map } from 'rxjs';
import {
  Auction,
  AuctionReserveDto,
  BidDto,
  UserAutoRebid,
} from 'src/app/articles-manager/interfaces/auction.interface';
import { AuctionViewService } from 'src/app/articles-manager/services/auction-view.service';
import { AccountDialogComponent } from '../../../login/components/account-dialog/account-dialog.component';
import { EnumBidResult } from '../../enums/bid-result.enum';
import { DialogArticleType } from '../../interfaces/article-bid.interface';
import { IBidResponse } from '../../interfaces/bid-response.interface';
import { AutobidDialogComponent } from '../dialogs/autobid-dialog/autobid-dialog.component';
import { BidBuyNowDialogComponent } from '../dialogs/bid-buy-now-dialog/bid-buy-now-dialog.component';
import { BidDialogComponent } from '../dialogs/bid-dialog/bid-dialog.component';
import { DeleteAutobidDialogComponent } from '../dialogs/delete-autobid-dialog/delete-autobid-dialog.component';

@Component({
  selector: 'app-article-bid',
  standalone: true,
  templateUrl: './article-bid.component.html',
  styleUrls: ['./article-bid.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    RouterModule,
    TranslateModule,
    BidDialogComponent,
  ],
})
export class ArticleBidComponent implements OnInit {
  idEventNow = input<number>(-1);
  slug = input<string | null>(null);
  isMessagePage = input<boolean>(false);
  selectedAuction = input<Auction>();
  inPreEventStatus = input<boolean>(false);
  isPreEventFinished = input<boolean>(false);
  minimumOffer = input<number>();
  sportIds = input<string>();
  competitionIds = input<string>();
  teamIds = input<string>();

  @Output('onSuccessBid') onSuccessBidEvent =
    new EventEmitter<DialogArticleType>();
  @Output('getHistory') getHistoryEvent = new EventEmitter<{}>();
  @Output('deleteAutoRebid') deleteAutoRebidEvent = new EventEmitter<{
    idAutoRebid: number;
  }>();
  @Output('stopRefresh') stopRefreshEvent = new EventEmitter<{}>();

  @ViewChild('bid') inputBid;

  faCalendarAlt = faCalendarAlt;
  faCheckCircle = faCheckCircle;
  faTimesCircle = faTimesCircle;
  faLock = faLock;
  faGavel = faGavel;
  isMemorabid = computed(
    () => (this.selectedAuction()?.idAuctionType ?? 1) == 1
  );

  subscription: Subscription;
  millisecondsInASecond = 1000;
  hoursInADay = 24;
  minutesInAHour = 60;
  secondsInAMinute = 60;
  public timeDifferenceFromStart = signal(0);

  public timeDifference = computed(() => {
    if (this.timeDifferenceFromStart() > 0) {
      return this.getTimeDifference(
        new Date(this.selectedAuction().endDateView + ':00')
      );
    } else {
      return this.getTimeDifference(
        new Date(this.selectedAuction().startDate + 'Z')
      );
    }
  });

  public secondsToEnd = computed(() =>
    Math.floor(
      (this.timeDifference() / this.millisecondsInASecond) %
        this.secondsInAMinute
    )
  );
  public minutesToEnd = computed(() =>
    Math.floor(
      (this.timeDifference() /
        (this.millisecondsInASecond * this.minutesInAHour)) %
        this.secondsInAMinute
    )
  );
  public hoursToEnd = computed(() =>
    Math.floor(
      (this.timeDifference() /
        (this.millisecondsInASecond *
          this.minutesInAHour *
          this.secondsInAMinute)) %
        this.hoursInADay
    )
  );
  public daysToEnd = computed(() =>
    Math.floor(
      this.timeDifference() /
        (this.millisecondsInASecond *
          this.minutesInAHour *
          this.secondsInAMinute *
          this.hoursInADay)
    )
  );
  isTimerToEndActive = signal(true);
  canMakeDirectOffer: boolean = false;

  private readonly bidService = inject(BidService);

  constructor(
    private modalUtils: ModalUtilsService,
    public authService: AuthService,
    private eventService: EventService,
    private _api: ApiService,
    private analyticsService: AnalyticsService,
    private messageService: MessageService,
    @Inject(PLATFORM_ID) private _platformId: Object
  ) {
    effect(
      (onCleanup) => {
        const startDate = new Date(this.selectedAuction().startDate + 'Z');
        this.timeDifferenceFromStart.set(
          new Date().getTime() - startDate.getTime()
        );

        if (isPlatformBrowser(this._platformId) && !this.isMessagePage()) {
          this.subscription = interval(1000).subscribe(() => {
            const startDate = new Date(this.selectedAuction().startDate + 'Z');

            this.timeDifferenceFromStart.set(
              new Date().getTime() - startDate.getTime()
            );
          });
        }

        onCleanup(() => {
          this.stopTimer();
        });
      },
      {
        allowSignalWrites: true,
      }
    );

    effect(
      () => {
        if (this.timeDifference() != null) {
          if (
            this.isTimerToEndActive() &&
            this.secondsToEnd() < 0 &&
            this.timeDifferenceFromStart() > 0 &&
            this.selectedAuction().isFinished
          ) {
            this.stopTimer();
            this.stopRefreshEvent.emit();
          }
        }
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  ngOnInit(): void {}

  ngOnDestroy() {
    this.stopTimer();
  }

  stopTimer() {
    if (this.subscription != null && this.subscription != undefined) {
      this.subscription.unsubscribe();
    }
    this.isTimerToEndActive.set(false);
  }

  getTimeDifference(date: Date) {
    return date.getTime() - new Date().getTime();
  }

  offersDialog() {
    if (!this.isMessagePage()) {
      this.getHistoryEvent.emit();
    }
  }

  makeBid(amount: number) {
    if (this.authService.isAuthenticated()) {
      if (this.idEventNow() === -1) {
        this.filterNoPaymentApiResponse(
          ApiServiceHolder.auction.canmakebid(this.selectedAuction().idAuction)
        ).subscribe((canMakeBidResponse) => {
          if (canMakeBidResponse.bidResult === EnumBidResult.Success) {
            const ref = this.modalUtils.openModal(BidDialogComponent, {
              size: 'md',
              keyboard: false,
            });
            ref.componentInstance.amount = amount;
            ref.componentInstance.inPreEventStatus = this.inPreEventStatus();
            ref.result.then((result) => {
              if (result) {
                this.onMakeBid(amount);
                this.inputBid.nativeElement.value = '';
              }
            });
          } else {
            throw new Error('Server error on bid');
          }
        });
      } else {
        const ref = this.modalUtils.openModal(BidDialogComponent, {
          size: 'md',
          keyboard: false,
        });
        ref.componentInstance.amount = amount;
        ref.componentInstance.inPreEventStatus = this.inPreEventStatus();
        ref.result.then((result) => {
          if (result) {
            this.onMakeBidEventNow(amount);
            this.inputBid.nativeElement.value = '';
          }
        });
      }
    } else {
      const ref = this.modalUtils.openModal(AccountDialogComponent, {
        size: 'md',
        keyboard: false,
      });
      if (this.slug() != null) {
        ref.componentInstance.nextPageRoute = 'eventnow/' + this.slug();
        ref.componentInstance.idEvent = this.idEventNow();
        ref.componentInstance.loginPage = false;
      }
    }
  }

  makeBidFromForm(formBid: NgForm) {
    this.makeBid(+formBid.value.bid);
  }

  makeBidFormByuNow() {
    const amount = this.selectedAuction().basePrice;
    const quantity = 1;

    if (this.authService.isAuthenticated() !== true) {
      const ref = this.modalUtils.openModal(AccountDialogComponent, {
        size: 'md',
        keyboard: false,
      });

      if (this.slug() != null) {
        ref.componentInstance.nextPageRoute = 'eventnow/' + this.slug();
        ref.componentInstance.idEvent = this.idEventNow();
        ref.componentInstance.loginPage = false;
      }
      return;
    }

    if (this.idEventNow() !== -1) {
      const ref = this.modalUtils.openModal(BidBuyNowDialogComponent, {
        size: 'md',
        keyboard: false,
      });
      ref.componentInstance.amount = amount;
      ref.componentInstance.quantity = quantity;
      ref.result.then((result) => {
        if (result) {
          this.onBuyNow(amount, quantity);
        }
      });
    } else {
      this.filterNoPaymentApiResponse(
        ApiServiceHolder.auction.canmakebid(this.selectedAuction().idAuction)
      ).subscribe((canMakeBidResponse) => {
        if (canMakeBidResponse.bidResult !== EnumBidResult.Success) {
          throw new Error('Server error on bid');
        }

        const ref = this.modalUtils.openModal(BidBuyNowDialogComponent, {
          size: 'md',
          keyboard: false,
        });
        ref.componentInstance.amount = amount;
        ref.result.then((result) => {
          if (result) {
            this.onBuyNow(amount, quantity);
          }
        });
      });
    }
  }

  makeAutoBid() {
    if (this.authService.isAuthenticated() == true) {
      if (this.idEventNow() == -1) {
        this.filterNoPaymentApiResponse(
          ApiServiceHolder.auction.canmakebid(this.selectedAuction().idAuction)
        ).subscribe((canMakeBidResponse) => {
          if (canMakeBidResponse.bidResult == EnumBidResult.Success) {
            const ref = this.modalUtils.openModal(AutobidDialogComponent, {
              size: 'lg',
              keyboard: false,
            });
            ref.componentInstance.isMemorabid = this.isMemorabid();
            ref.componentInstance.inPreEventStatus = this.inPreEventStatus();
            ref.componentInstance.minimumOffer = this.minimumOffer();
            ref.componentInstance.rebidMaxOfferPrice =
              this.selectedAuction().rebidMaxOfferPrice;
            ref.result.then((result) => {
              if (result > 0) {
                this.onMakeAutoBid(+result);
              }
            });
          } else {
            throw new Error('Server error on bid');
          }
        });
      } else {
        const ref = this.modalUtils.openModal(AutobidDialogComponent, {
          size: 'lg',
          keyboard: false,
        });
        ref.componentInstance.isMemorabid = this.isMemorabid();
        ref.componentInstance.inPreEventStatus = this.inPreEventStatus();
        ref.componentInstance.minimumOffer = this.minimumOffer();
        ref.componentInstance.rebidMaxOfferPrice =
          this.selectedAuction().rebidMaxOfferPrice;
        ref.result.then((result) => {
          if (result > 0) {
            this.onMakeAutoBid(+result);
          }
        });
      }
    } else {
      const ref = this.modalUtils.openModal(AccountDialogComponent, {
        size: 'md',
        keyboard: false,
      });
      if (this.slug() != null) {
        ref.componentInstance.nextPageRoute = 'eventnow/' + this.slug();
        ref.componentInstance.idEvent = this.idEventNow();
        ref.componentInstance.loginPage = false;
      }
    }
  }

  deleteAutoRebid(idAutoRebid: number) {
    const ref = this.modalUtils.openModal(DeleteAutobidDialogComponent, {
      size: 'lg',
      keyboard: false,
    });
    ref.componentInstance.inPreEventStatus = this.inPreEventStatus();
    ref.result.then((result) => {
      if (result) {
        this.deleteAutoRebidEvent.emit({ idAutoRebid: idAutoRebid });
      }
    });
  }

  private filterNoPaymentApiResponse(
    apiRequest: IApiRequest
  ): Observable<IBidResponse> {
    return this._api.request<IBidResponse>(apiRequest).pipe(
      map((result) => {
        if (result.bidResult == EnumBidResult.NoPaymentMethod) {
          let ref = this.modalUtils.openModal(NoPaymentMethodDialogComponent, {
            size: 'lg',
            keyboard: false,
            backdrop: 'static',
          });
          ref.result.then(
            () => {
              location.replace(result.stripeCheckoutSessionUrl);
            },
            () => {
              /* do nothing on close click */
            }
          );
        }

        return result;
      }),
      filter((result) => result.bidResult !== EnumBidResult.NoPaymentMethod)
    );
  }

  private onMakeBid(offeredPrice: number): void {
    const bidDto: BidDto = {
      idAuction: this.selectedAuction().idAuction,
      offeredPrice,
    };
    this.filterNoPaymentApiResponse(
      ApiServiceHolder.auction.bid(bidDto)
    ).subscribe((bidResponse) => {
      if (bidResponse.bidResult == EnumBidResult.Success) {
        this.analyticsService.trackBid(
          this.selectedAuction().idAuction,
          offeredPrice,
          this.slug()
        );
        // todo evento per ricaricare i dati dell'asta
        this.onSuccessBidEvent.emit('BID');
      } else {
        throw new Error('Server error on bid');
      }
    });
  }

  private onBuyNow(offeredPrice: number, quantity: number): void {
    const bidDto: BidDto = {
      idAuction: this.selectedAuction().idAuction,
      offeredPrice,
      articleQty: quantity,
    };
    this.filterNoPaymentApiResponse(
      ApiServiceHolder.auction.buyNow(bidDto)
    ).subscribe((bidResponse) => {
      if (bidResponse.bidResult == EnumBidResult.Success) {
        this.analyticsService.trackBid(
          this.selectedAuction().idAuction,
          offeredPrice,
          this.slug()
        );
        // todo evento per ricaricare i dati dell'asta
        this.onSuccessBidEvent.emit('BUY_NOW');
        this.messageService.updateCountUnreadMessages();
      } else {
        throw new Error('Server error on bid');
      }
    });
  }

  private onMakeBidEventNow(offeredPrice: number): void {
    const bidDto: BidDto = {
      idAuction: this.selectedAuction().idAuction,
      offeredPrice,
    };
    this.eventService.bid(bidDto).subscribe((bidResponse) => {
      if (bidResponse.bidResult == EnumBidResult.Success) {
        this.analyticsService.trackBid(
          this.selectedAuction().idAuction,
          offeredPrice,
          this.slug()
        );
        this.onSuccessBidEvent.emit('BID');
      } else {
        throw new Error('Server error on bid');
      }
    });
  }

  private onMakeAutoBid(maxOfferPrice: number): void {
    const userAutoRebid: UserAutoRebid = {
      idAuction: this.selectedAuction().idAuction,
      maxOfferPrice,
    };
    if (this.idEventNow() == -1) {
      this.filterNoPaymentApiResponse(
        ApiServiceHolder.auction.autoRebid(userAutoRebid)
      ).subscribe(() => {
        this.onSuccessBidEvent.emit('BID');
      });
    } else {
      this._api
        .request<IBidResponse>(
          ApiServiceHolder.memorabidevent.autoRebid(userAutoRebid)
        )
        .subscribe(() => {
          this.onSuccessBidEvent.emit('BID');
        });
    }
  }

  makeBidReservePrice() {
    this.bidService.makeBidReservePrice(
      this.selectedAuction().idAuction,
      this.onSuccessBidEvent
    );
  }

  makeOfferMessage() {
    this.bidService.makeOfferMessage(this.selectedAuction().idAuction);
  }
}
